// life-os-sync-internals.ts — standalone (non-hook) helpers for the LifeOS sync/
// persistence layer. Extracted from useLifeOs.ts so that hook stays focused on
// orchestration. No React, no closure state — pure functions + constants.

import { loadCloudSyncRuntime } from "../lib/sync-runtime";
import type { CloudSyncRuntime } from "../lib/sync-runtime";
import type { LifeOsState } from "../types";

export type NativeListenerHandle = { remove: () => Promise<void> };

type CapacitorHost = typeof globalThis & {
  Capacitor?: {
    isNativePlatform?: () => boolean;
  };
};

export function isNativeCapacitorPlatform() {
  return (globalThis as CapacitorHost).Capacitor?.isNativePlatform?.() === true;
}

export async function addNativeForegroundRetry(
  onActive: () => void,
): Promise<NativeListenerHandle | undefined> {
  if (!isNativeCapacitorPlatform()) {
    return undefined;
  }

  const { App: CapacitorApp } = await import("@capacitor/app");
  return CapacitorApp.addListener("appStateChange", ({ isActive }) => {
    if (isActive) {
      onActive();
    }
  });
}

export function loadSyncRuntime(): Promise<CloudSyncRuntime> {
  return loadCloudSyncRuntime();
}

export const INITIAL_SYNC_TIMEOUT_MS = 10_000;
export const SYNC_OPERATION_TIMEOUT_MS = 10_000;
export const INDEXED_REFERENCE_ITEM_THRESHOLD = 2_000;

export function persistedItemCount(state: LifeOsState) {
  return (
    state.tasks.length +
    state.projects.length +
    state.goals.length +
    state.habits.length +
    state.notes.length +
    state.transactions.length +
    state.budgets.length +
    state.inbox.length +
    state.events.length +
    state.recurring.length +
    state.accounts.length +
    state.dailyPlans.length +
    state.dailyReviews.length +
    state.weeklyReviews.length +
    state.templates.length +
    state.currencyRates.length +
    state.financeCategories.length +
    state.dirtyItems.length +
    state.deletedItems.length
  );
}

export function withSyncTimeout<T>(operation: Promise<T>): Promise<T> {
  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(
      () => reject(new Error("Sync operation timed out")),
      SYNC_OPERATION_TIMEOUT_MS,
    );
  });
  return Promise.race([operation, timeout]).finally(() => window.clearTimeout(timeoutId));
}
